"use client";

/**
 * The /alerts page's building blocks shared with other surfaces that
 * present a subscription the same way (the developers' offers page's
 * bulletin card): the alert type's scene, its channels, the panel frame
 * with its header line, the row styles, the named way out and the
 * on/off switch. Moved out of app/[locale]/alerts/AlertTypeSections
 * (which re-exports them) so a page can use them without the whole
 * alerts page's code.
 */
import { Bell, ChevronRight, Mail, Send } from "lucide-react";
import Illo from "@/components/Illo";
import Link from "@/lib/localized-link";
import { useT } from "@/lib/locale-context";
import type { TranslationKey } from "@/lib/i18n";

// One illustration per alert type: components/alerts/type-art.ts (a plain
// module, shared with the server-rendered alerts landing).
import { TYPE_ART, type AlertKind } from "./type-art";
export { TYPE_ART };
export type { AlertKind };

/** The scene zoomed onto its subject: the source art keeps wide margins,
 *  which at thumbnail size left a speck in an empty tile. */
export function TypeArt({ kind, className = "h-12 w-12", big = false }: { kind: AlertKind; className?: string; big?: boolean }) {
  return (
    <span className={`relative inline-block shrink-0 overflow-hidden rounded-lg ${className}`}>
      <Illo base={`/illustrations/alerts/${TYPE_ART[kind]}`} className={`h-full w-full object-cover ${big ? "scale-[1.4]" : "scale-[1.65]"}`} />
    </span>
  );
}

// ── Shared bits ──────────────────────────────────────────────────────

export type ChannelKey = "telegram" | "push" | "email";
export const CHANNEL_ICON = { telegram: Send, push: Bell, email: Mail };
export const CHANNEL_LABEL: Record<ChannelKey, TranslationKey> = {
  telegram: "alerts.channel.telegram",
  push: "alerts.channel.push",
  email: "alerts.channel.email_fallback",
};

/** Which channels each type uses (spelled out in the help drawer). */
export const TYPE_CHANNELS: Record<AlertKind, ChannelKey[]> = {
  search: ["telegram", "push", "email"],
  watch: ["telegram", "push"],
  residence: ["push"],
  auction: ["telegram", "push", "email"],
  property: ["telegram", "push"],
  digest: ["telegram", "push", "email"],
  // Inside the Monday digest for its readers, alone for the others.
  new_residences: ["telegram", "push", "email"],
  // The monthly alert and the followed developers' morning alert: push
  // too since 2026-10-22.
  bulletin: ["telegram", "push", "email"],
};

/** The channels as small icons; the words are for screen readers and
 *  the help drawer. */
export function ChannelIcons({ list }: { list: ChannelKey[] }) {
  const t = useT();
  return (
    <span className="hidden items-center gap-1 text-gray-400 sm:inline-flex">
      <span className="sr-only">{t("alerts.hub.channels")} {list.map((c) => t(CHANNEL_LABEL[c])).join(", ")}</span>
      {list.map((c) => {
        const I = CHANNEL_ICON[c];
        return <I key={c} className="h-3 w-3" aria-hidden />;
      })}
    </span>
  );
}

/** One alert type: a header line, then flush rows. */
export function Panel({
  id, kind, title, count, action, children,
}: {
  id: string;
  kind: AlertKind;
  title: string;
  count?: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 overflow-hidden rounded-2xl border border-gray-200 bg-white" aria-labelledby={`${id}-h`}>
      {/* The scene is the card's face (owner, 2026-10-07): 80 px on the
          left, title with its count and channels stacked beside it. */}
      {/* Phones: 48 px art and a one-line title, so a card with one row
          is not a tall rectangle around it (owner, 2026-10-09). */}
      <div className={`flex items-center gap-3 px-3 py-1.5 sm:py-2 ${children ? "border-b border-gray-100" : ""}`}>
        <TypeArt kind={kind} className="h-12 w-12 rounded-lg sm:h-20 sm:w-20 sm:rounded-xl" big />
        <div className="min-w-0 flex-1">
          <h2 id={`${id}-h`} className="line-clamp-2 text-sm font-semibold leading-snug text-gray-900 sm:text-base">{title}</h2>
          <div className="mt-1 flex items-center gap-2">
            {count != null && <span className="shrink-0 text-xs font-semibold text-gray-400 tabular-nums">{count}</span>}
            <ChannelIcons list={TYPE_CHANNELS[kind]} />
          </div>
        </div>
        {action && <div className="flex shrink-0 items-center gap-1.5">{action}</div>}
      </div>
      {children}
    </section>
  );
}

export const ROW = "flex items-center gap-3 py-2 pl-4 pr-2";
export const ROW_ACTION = "inline-flex h-9 w-9 items-center justify-center rounded-lg sm:h-7 sm:w-7 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-40";
export const ROW_DELETE = "inline-flex h-9 w-9 items-center justify-center rounded-lg sm:h-7 sm:w-7 text-gray-400 transition-colors hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40 dark:hover:text-rose-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-40";
/** A header's quiet text action ("All", "Open auctions"). */
export const SMALL_BTN = "inline-flex h-7 items-center gap-1 rounded-lg border border-gray-200 px-2.5 text-xs font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:opacity-60";

/** The card's one way out, named: "Open my watchlist", never "All"
 *  beside the art (owner, 2026-10-09). */
export function PanelFoot({ href, label }: { href: string; label: string }) {
  return (
    <Link href={href} className="no-scale flex items-center justify-between border-t border-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-50">
      {label}
      <ChevronRight className="h-4 w-4" aria-hidden />
    </Link>
  );
}

/** The alert type's on/off switch (the header's control on /alerts): a
 *  thumb-sized 44 x 24 switch on phones, the compact one beside a pointer. */
export function AlertSwitch({ on, label, onToggle, disabled = false }: {
  on: boolean;
  label: string;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onToggle}
      disabled={disabled}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-60 sm:h-5 sm:w-9 ${on ? "bg-indigo-600" : "bg-gray-300"}`}
    >
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all sm:h-4 sm:w-4 ${on ? "left-[22px] sm:left-[18px]" : "left-0.5"}`} />
    </button>
  );
}
